<template>
  <div class="container px-0" v-if="$gate.isAdmin()">
    <!-- Toolbar -->
    <div class="d-flex justify-content-end mb-3">
      <button
        type="button"
        :class="['btn','btn-sm','fw-bold', stare_incarca_fisiere === 1 ? 'btn-light-warning' : 'btn-light-success']"
        @click="switchMode"
      >
        <i :class="stare_incarca_fisiere === 1 ? 'fa-solid fa-eye fs-6 me-1' : 'fa-solid fa-upload fs-6 me-1'"></i>
        {{ stare_incarca_fisiere === 1 ? 'Vizualizare fișiere' : 'Încărcare fișiere' }}
      </button>
    </div>

    <!-- Date Range Card -->
    <div class="card mb-4" v-if="stare_vizualizare_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Filtrare după perioadă</h5>
      </div>
      <div class="card-body">
        <form @submit.prevent="applyFilters">
          <!-- Row 1: Date range -->
          <div class="row g-3 align-items-end mb-2">
            <div class="col-12 col-md-3">
              <label class="form-label">Data de la</label>
              <div class="position-relative">
                <input
                  ref="dateFromRef"
                  v-model="dateFrom"
                  type="date"
                  class="form-control pe-5"
                  :max="dateTo || undefined"
                />
                <button
                  type="button"
                  class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('from')"
                  aria-label="Deschide calendar"
                >
                  📅
                </button>
              </div>
            </div>
            <div class="col-12 col-md-3">
              <label class="form-label">Data până la</label>
              <div class="position-relative">
                <input
                  ref="dateToRef"
                  v-model="dateTo"
                  type="date"
                  class="form-control pe-5"
                  :min="dateFrom || undefined"
                />
                <button
                  type="button"
                  class="date-picker-btn btn btn-outline-secondary btn-sm"
                  @click="openNativePicker('to')"
                  aria-label="Deschide calendar"
                >
                  📅
                </button>
              </div>
            </div>
          </div>

          <!-- Row 2: Search + Status filters -->
          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-6 col-lg-5">
              <label class="form-label">Caută după nume</label>
              <input
                v-model.trim="filesFilters.q"
                type="text"
                class="form-control"
                placeholder="ex: contract, notificare"
              />
            </div>
            <div class="col-6 col-md-3 col-lg-3 d-flex align-items-center">
              <div class="form-check me-4">
                <input class="form-check-input" type="checkbox" id="filterErrors" v-model="filesFilters.onlyErrors">
                <label class="form-check-label" for="filterErrors">
                  Cu erori
                </label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="filterWarnings" v-model="filesFilters.onlyWarnings">
                <label class="form-check-label" for="filterWarnings">
                  Cu avertizări
                </label>
              </div>
            </div>
            <div class="col-12 col-md-3 col-lg-4 d-flex gap-2">
              <button type="submit" class="btn btn-primary w-100" :disabled="!hasActiveFilters">
                Aplică
              </button>
              <button type="button" class="btn btn-light-secondary fw-bold w-100" @click="resetFilters" :disabled="!hasActiveFilters">
                <i class="fa-solid fa-rotate-left me-1"></i>
                Resetare
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Upload Card -->
    <div class="card" v-if="stare_incarca_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Încărcare alte documente (multiple)</h5>
      </div>
      <div class="card-body">
        <div
          class="border rounded-3 p-4 text-center mb-3"
          :class="{'border-primary': isDragging, 'opacity-50': uploading}"
          :style="{ pointerEvents: uploading ? 'none' : 'auto' }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <p class="mb-2">Trage aici fișierele sau</p>
          <div>
            <label class="btn btn-outline-primary">
              Selectează fișiere
              <input
                ref="fileInput"
                type="file"
                class="d-none"
                multiple
                accept=".pdf,image/*,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                @change="onFileChange"
              />
            </label>
          </div>
          <small class="text-muted d-block mt-2">
            Acceptăm PDF, imagini (JPG/PNG/WEBP) și documente Office (DOC/DOCX/XLS/XLSX). Maxim 20MB/fișier.
          </small>
        </div>

        <!-- Previews -->
        <div v-if="files.length" class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Fișiere selectate ({{ files.length }})</h6>
            <button class="btn btn-sm btn-light border" @click="clearAll">Șterge toate</button>
          </div>

          <div class="row g-3">
            <div
              v-for="f in files"
              :key="f.id"
              class="col-6 col-md-4 col-lg-3"
            >
              <div class="border rounded-3 p-2 h-100 d-flex flex-column">
                <div class="ratio ratio-1x1 mb-2 bg-light d-flex align-items-center justify-content-center overflow-hidden rounded">
                  <img
                    v-if="f.isImage && f.previewUrl"
                    :src="f.previewUrl"
                    alt="preview"
                    class="img-fluid"
                  />
                  <img
                    v-else-if="f.isPdf && f.thumbUrl"
                    :src="f.thumbUrl"
                    alt="pdf thumbnail"
                    class="img-fluid"
                  />
                  <div v-else-if="f.isPdf && f.thumbPending" class="text-center p-3 small w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                    <div class="spinner-border spinner-border-sm mb-2" role="status"></div>
                    <div>Generez miniatura… (PDF mare?)</div>
                  </div>
                  <div v-else class="text-center p-3 small">
                    <div class="fw-semibold">{{ f.ext.toUpperCase() }}</div>
                    <div class="text-muted">{{ humanSize(f.size) }}</div>
                  </div>
                </div>
                <div class="small flex-grow-1">
                  <div class="fw-semibold text-truncate" :title="f.name">{{ f.name }}</div>
                  <div class="text-muted">{{ friendlyType(f) }}</div>
                </div>
                <div class="mt-2 d-flex gap-2">
                  <a
                    v-if="f.previewUrl || f.isPdf || f.isOffice"
                    href="#"
                    class="btn btn-sm btn-outline-secondary w-100"
                    @click.prevent="openPreview(f.id)"
                  >Previzualizare</a>
                  <button
                    class="btn btn-sm btn-outline-danger w-100"
                    @click="removeFile(f.id)"
                  >Șterge</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end align-items-center">
          <button
            class="btn btn-success"
            :disabled="!files.length || uploading"
            @click="emitUpload"
          >
            {{ uploading ? 'Se încarcă…' : 'Încarcă fișierele' }}
          </button>
          <div v-if="uploading" class="ms-3 d-flex align-items-center gap-2">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <small>{{ totalProgress }}%</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal (local selection) -->
    <div v-if="showPreview" class="preview-modal d-flex align-items-center justify-content-center">
      <div class="preview-backdrop" @click="closePreview"></div>
      <div class="preview-content card shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong class="text-truncate" :title="currentFile?.name">{{ currentFile?.name }}</strong>
          <button class="btn btn-sm btn-light" @click="closePreview">Închide</button>
        </div>
        <div class="card-body p-0">
          <!-- Image preview -->
          <div v-if="currentFile && currentFile.isImage" class="p-3 text-center">
            <img :src="currentFile.previewUrl" alt="preview" class="img-fluid" />
          </div>
          <!-- PDF preview -->
          <div v-else-if="currentFile && currentFile.isPdf" class="pdf-preview">
            <iframe :src="currentFile.previewUrl" title="PDF preview"></iframe>
          </div>
          <!-- Office fallback preview -->
          <div v-else-if="currentFile && currentFile.isOffice" class="p-4">
            <p class="mb-2">Preview-ul direct în browser pentru fișiere Word/Excel este limitat fără un URL public.</p>
            <ul class="small text-muted mb-3">
              <li>Poți <strong>descărca</strong> fișierul pentru a-l deschide local.</li>
              <li>Sau oferă un URL public pentru a folosi Office Web Viewer.</li>
            </ul>
            <div class="d-flex gap-2">
              <a :href="currentFile.previewUrl" download class="btn btn-primary">Descarcă</a>
              <a
                v-if="currentFile.serverUrl"
                :href="'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(currentFile.serverUrl)"
                target="_blank"
                rel="noopener"
                class="btn btn-outline-secondary"
              >Deschide în Office Web Viewer</a>
            </div>
          </div>
          <!-- Generic fallback -->
          <div v-else class="p-4">
            <p>Acest tip de fișier nu are o previzualizare în browser. Îl poți descărca mai jos.</p>
            <a :href="currentFile?.previewUrl" download class="btn btn-primary">Descarcă</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Vizualizare Fișiere Card -->
    <div class="card" v-if="stare_vizualizare_fisiere === 1">
      <div class="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 class="mb-0">Fișiere încărcate (alte documente)</h5>
      </div>
      <div class="card-body">
        <div v-if="loadingFiles" class="p-4 text-center">
          <div class="spinner-border" role="status"></div>
        </div>
        <div v-else>
          <div v-if="!groupedFiles.length" class="p-4 text-center text-muted">
            Nu s-au găsit fișiere.
          </div>

          <div v-else class="d-flex flex-column gap-4">
            <!-- Group per month -->
            <div v-for="grp in groupedFiles" :key="grp.key" class="file-month-group">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0">{{ grp.label }}</h6>
                <small class="text-muted">{{ grp.items.length }} fișier{{ grp.items.length === 1 ? '' : 'e' }}</small>
              </div>

              <!-- Thumbnails grid -->
              <div class="thumb-grid">
                <div
                  v-for="it in grp.items"
                  :key="it.id"
                  class="thumb-card"
                >
                  <div class="thumb-media">
                    <img
                      :src="serverThumbSrc(it)"
                      :alt="it.original_name"
                      class="img-fluid"
                      loading="lazy"
                    />
                    <!-- status overlay -->
                    <div
                      class="thumb-status"
                      :class="statusClass(it)"
                      :title="statusLabel(it)"
                      v-if="typeof it.stare_fisier !== 'undefined'"
                    >
                      <i :class="statusIcon(it)"></i>
                    </div>
                  </div>
                  <div class="thumb-body">
                    <div class="thumb-title" :title="it.original_name">{{ it.original_name }}</div>
                    <div class="thumb-meta text-muted">{{ formatDate(it.created_at) }} · {{ humanSize(it.size || 0) }}</div>
                  </div>
                  <div class="thumb-actions">
                    <button class="btn btn-sm btn-light-primary fw-bold" @click="openServerPreview(it)">
                      <i class="fa-solid fa-eye fs-5 me-1"></i>
                      Previzualizare
                    </button>
                    <a class="btn btn-sm btn-light-primary fw-bold" :href="`${apiBase}/download_altedocts/${it.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Server Preview Modal (split layout) -->
      <div v-if="serverPreviewItem" class="preview-modal d-flex align-items-center justify-content-center">
        <div class="preview-backdrop" @click="closeServerPreview"></div>
        <div class="preview-content card shadow-lg">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong class="text-truncate" :title="serverPreviewItem.original_name">{{ serverPreviewItem.original_name }}</strong>
            <div class="d-flex gap-2">
              <a class="btn btn-sm btn-light-primary fw-bold" :href="`${apiBase}/download_altedocts/${serverPreviewItem.id}?firma_id=${firmaId}`">
                <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                Descarcare
              </a>
              <button class="btn btn-sm btn-light-secondary fw-bold" @click="closeServerPreview">
                <i class="fa-regular fa-circle-xmark fs-5 me-1"></i>
                Închide
              </button>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="row g-0 preview-split">
              <!-- Left: Preview -->
              <div
                class="col-12 col-lg-7 preview-split-left"
                :class="{ 'zoom-enabled': serverZoomed, 'dragging': isPreviewDragging }"
                ref="previewLeft"
                @mousedown="onPreviewDragStart"
              >
                <!-- Image preview -->
                <div
                  v-if="isImageMime(serverPreviewItem.mime)"
                  class="pannable p-3 h-100"
                  :class="{ 'centered': !serverZoomed }"
                >
                  <img
                    ref="previewImg"
                    :src="`${apiBase}/stream_altedocts/${serverPreviewItem.id}?firma_id=${firmaId}`"
                    alt="preview"
                    class="preview-image"
                    :class="{ 'is-zoomed': serverZoomed }"
                    :style="previewImgStyle"
                    title="Click pentru zoom (1× / 2× / 3×). Ține apăsat și trage pentru a deplasa."
                    draggable="false"
                    @load="onPreviewImgLoaded"
                    @dragstart.prevent
                    @click="toggleServerZoom($event)"
                    @wheel.prevent="onWheelPreview($event)"
                  />
                </div>
                <!-- PDF preview -->
                <div v-else-if="isPdfMime(serverPreviewItem.mime)" class="pdf-preview h-100">
                  <iframe :src="`${apiBase}/stream_altedocts/${serverPreviewItem.id}?firma_id=${firmaId}`" title="PDF preview"></iframe>
                </div>
                <!-- Office fallback -->
                <div v-else-if="isOfficeMime(serverPreviewItem.mime)" class="p-4 h-100 d-flex align-items-center justify-content-center text-center">
                  <div>
                    <p class="mb-2">Preview-ul direct în browser pentru Word/Excel este limitat în modul securizat.</p>
                    <p class="small text-muted mb-3">Poți descărca fișierul pentru a-l deschide local.</p>
                    <a class="btn btn-light-primary fw-bold" :href="`${apiBase}/download_altedocts/${serverPreviewItem.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
                <!-- Generic fallback -->
                <div v-else class="p-4 h-100 d-flex align-items-center justify-content-center text-center">
                  <div>
                    <p>Acest tip de fișier nu are previzualizare. Descarcă pentru vizualizare.</p>
                    <a class="btn btn-light-primary fw-bold" :href="`${apiBase}/download_altedocts/${serverPreviewItem.id}?firma_id=${firmaId}`">
                      <i class="fa-solid fa-cloud-arrow-down fs-5 me-1"></i>
                      Descarcare
                    </a>
                  </div>
                </div>
              </div>

              <!-- Right: Details + form (admin edits) -->
              <div class="col-12 col-lg-5 preview-split-right">
                <form @submit.prevent="updateServerItem" @keydown="filechange_form.onKeydown($event)">
                  <div class="p-3 p-lg-4">
                    <!-- Open raw file in pop-up -->
                    <div class="text-center mb-3">
                      <button
                        class="btn btn-sm btn-light-primary fw-bold"
                        @click="openPopupPreview(serverPreviewItem)"
                        type="button"
                        title="Deschide fișierul într-un pop-up"
                      >
                        <i class="fa-solid fa-up-right-from-square fs-6 me-1"></i>
                        Deschide fișierul
                      </button>
                    </div>

                    <!-- Nume fișier -->
                    <div class="mb-3">
                      <div class="text-muted small">Nume fișier</div>
                      <div class="fw-semibold text-break">{{ serverPreviewItem.original_name || '—' }}</div>
                    </div>

                    <!-- Stare fișier -->
                    <div class="mb-3">
                      <div class="text-muted small d-flex align-items-center justify-content-between">
                        <span>Stare fișier</span>
                        <span>
                          <span class="badge" :class="statusBadgeClass(serverPreviewItem)">
                            <i :class="statusIcon(serverPreviewItem)" class="me-1"></i>
                            {{ statusLabel(serverPreviewItem) }}
                          </span>
                        </span>
                      </div>
                      <div class="d-flex align-items-center gap-2 mt-2">
                        <multiselect
                          v-model="filechange_form.stare_fisier"
                          :options="stateOptions"
                          label="label"
                          track-by="value"
                          :reduce="opt => opt.value"
                          :searchable="false"
                          :allow-empty="true"
                          :show-labels="false"
                          placeholder="Alege starea"
                          class="flex-grow-1"
                        />
                      </div>
                      <div
                        v-if="filechange_form.errors && filechange_form.errors.has('stare_fisier')"
                        class="text-danger small mt-1"
                        v-html="filechange_form.errors.get('stare_fisier')"
                      ></div>
                    </div>

                    <!-- Mărime + Data upload -->
                    <div class="mb-3">
                      <div class="row g-3 align-items-start">
                        <div class="col-12 col-sm-6">
                          <div class="text-muted small">Mărime</div>
                          <div class="fw-semibold">{{ humanSize(serverPreviewItem.size || 0) }}</div>
                        </div>
                        <div class="col-12 col-sm-6">
                          <div class="text-muted small">Data upload</div>
                          <div class="fw-semibold">{{ formatDate(serverPreviewItem.created_at) }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- Lucrat de -->
                    <div class="mb-3">
                      <div class="text-muted small">Lucrat de</div>
                      <div class="fw-semibold">{{ serverPreviewItem.lucrat_de_nume || '—' }}</div>
                    </div>

                    <!-- Alte observații -->
                    <div class="mb-3">
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="text-muted small">Alte observații</div>
                        <button
                          type="button"
                          class="btn btn-sm btn-light border"
                          @click="editObservatii = !editObservatii"
                          :title="editObservatii ? 'Anulează editarea' : 'Editează'"
                        >
                          <i class="fa-solid" :class="editObservatii ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                        </button>
                      </div>
                      <div v-if="!editObservatii" class="text-break">
                        {{ serverPreviewItem.alte_observatii || '—' }}
                      </div>
                      <div v-else class="mt-2">
                        <textarea
                          class="form-control"
                          rows="3"
                          v-model.trim="filechange_form.alte_observatii"
                          placeholder="Adaugă observații..."
                        ></textarea>
                        <div
                          v-if="filechange_form.errors && filechange_form.errors.has('alte_observatii')"
                          class="text-danger small mt-1"
                          v-html="filechange_form.errors.get('alte_observatii')"
                        ></div>
                      </div>
                    </div>

                    <!-- Note -->
                    <div>
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="text-muted small">Note</div>
                        <button
                          type="button"
                          class="btn btn-sm btn-light border"
                          @click="editNote = !editNote"
                          :title="editNote ? 'Anulează editarea' : 'Editează'"
                        >
                          <i class="fa-solid" :class="editNote ? 'fa-xmark' : 'fa-pen-to-square'"></i>
                        </button>
                      </div>
                      <div v-if="!editNote" class="text-break">
                        {{ serverPreviewItem.note || '—' }}
                      </div>
                      <div v-else class="mt-2">
                        <textarea
                          class="form-control"
                          rows="3"
                          v-model.trim="filechange_form.note"
                          placeholder="Adaugă note..."
                        ></textarea>
                        <div
                          v-if="filechange_form.errors && filechange_form.errors.has('note')"
                          class="text-danger small mt-1"
                          v-html="filechange_form.errors.get('note')"
                        ></div>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="mt-4 d-flex justify-content-end">
                      <button type="submit" class="btn btn-primary fw-bold" :disabled="filechange_form.busy">
                        <span v-if="filechange_form.busy" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Actualizare
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div> <!-- /row -->
          </div>
        </div>
      </div> <!-- /Server Preview Modal -->
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'

import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

if (pdfjsLib && pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl
}

export default {
  name: 'AdminIncarcariAlteDoc',
  components: { Multiselect },
  props: {
    firmaId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['submit-range'],
  data() {
    return {
      uploading: false,
      totalProgress: 0,

      dateFrom: '',
      dateTo: '',
      isDragging: false,
      files: [], // local selection previews
      showPreview: false,
      currentFileId: null,
      _pdfQueue: [],
      _pdfActive: 0,
      _pdfMax: 3,
      _pdfStarted: false,

      // Modes
      stare_incarca_fisiere: 0,
      stare_vizualizare_fisiere: 1,
      loadingFiles: false,
      filesList: [],
      filesFilters: {
        q: '',
        per_page: 20,
        page: 1,
        onlyErrors: false,
        onlyWarnings: false,
      },
      serverPreviewItem: null,
      serverZoomed: false,
      serverZoomScale: 1,
      serverZoomLevel: 0,         // 0: 1x, 1: 2x, 2: 3x
      zoomScales: [1, 2, 3],
      isPreviewDragging: false,
      previewDragStartX: 0,
      previewDragStartY: 0,
      previewScrollStartLeft: 0,
      previewScrollStartTop: 0,
      suppressNextClick: false,
      movedDuringDrag: false,
      serverThumbs: {}, // { [id]: { src: string, pending: boolean } }
      serverImgNaturalW: 0,
      serverImgNaturalH: 0,

      stateOptions: [
        { value: 0, label: 'În așteptare' },
        { value: 1, label: 'Succes' },
        { value: 2, label: 'Eșec' },
        { value: 3, label: 'Avertizare' },
      ],

      filechange_form: Form.make({
        id: '',
        stare_fisier: { label: 'În așteptare', value: 0 },
        alte_observatii: '',
        note: '',
      }),
      editObservatii: false,
      editNote: false,
    }
  },
  computed: {
    currentFile() {
      return this.files.find(f => f.id === this.currentFileId) || null
    },
    groupedFiles() {
      const groups = {}
      for (const it of this.filesList) {
        const key = this._extractMonthKey(it) || this._monthKeyFromDate(it.created_at)
        if (!groups[key]) groups[key] = { key, label: this._labelFromMonthKey(key), items: [] }
        groups[key].items.push(it)
      }
      const arr = Object.values(groups)
      arr.sort((a, b) => {
        const [am, ay] = a.key.split('_').map(Number)
        const [bm, by] = b.key.split('_').map(Number)
        if (ay !== by) return by - ay
        return bm - am
      })
      return arr
    },
    hasActiveFilters() {
      const hasDates = !!(this.dateFrom || this.dateTo)
      const hasText  = !!(this.filesFilters.q && this.filesFilters.q.trim() !== '')
      const hasFlags = !!(this.filesFilters.onlyErrors || this.filesFilters.onlyWarnings)
      return hasDates || hasText || hasFlags
    },
    previewImgStyle() {
      if (!this.serverZoomed) {
        return {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain'
        }
      }
      const nW = this.serverImgNaturalW || 0
      const scale = this.serverZoomScale || 1
      if (nW > 0) {
        return {
          width: (nW * scale) + 'px',
          maxWidth: 'none',
          height: 'auto',
        }
      }
      const pct = Math.max(100, Math.min(300, Math.round(scale * 100)))
      return { width: pct + '%', maxWidth: 'none', height: 'auto' }
    },
    apiBase() {
      return '/api/administrare/work/fisiere'
    },
  },
  mounted() {
    this.fetchFiles(1)
  },
  beforeUnmount() {
    this.files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl) })
  },
  methods: {
    applyFilters() {
      if (!this.hasActiveFilters) return
      if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
        const tmp = this.dateFrom
        this.dateFrom = this.dateTo
        this.dateTo = tmp
      }
      this.emitSubmit()
      this.fetchFiles(1)
    },
    openNativePicker(which) {
      try {
        const el = which === 'from' ? this.$refs.dateFromRef : this.$refs.dateToRef
        if (!el) return
        if (typeof el.showPicker === 'function') {
          el.showPicker()
        } else {
          el.focus()
          el.click && el.click()
        }
      } catch {}
    },
    switchMode() {
      if (this.stare_incarca_fisiere === 1) {
        this.stare_incarca_fisiere = 0
        this.stare_vizualizare_fisiere = 1
        this.fetchFiles(1)
      } else {
        this.stare_incarca_fisiere = 1
        this.stare_vizualizare_fisiere = 0
      }
    },
    async fetchFiles(page = 1) {
      this.loadingFiles = true
      this.filesFilters.page = page
      try {
        const { q, per_page, onlyErrors, onlyWarnings } = this.filesFilters
        const params = { page, per_page }
        if (q) params.q = q
        if (this.dateFrom) params.from = this.dateFrom
        if (this.dateTo)   params.to   = this.dateTo
        if (onlyErrors)   params.only_errors   = 1
        if (onlyWarnings) params.only_warnings = 1
        params.firma_id = this.firmaId

        const { data } = await axios.get(`${this.apiBase}/vizualizare_altedocts`, { params, withCredentials: true })
        let list = data?.data || []

        // fallback pentru filtrele de stare pe client
        if (onlyErrors || onlyWarnings) {
          list = list.filter(it => {
            const s = Number(it.stare_fisier)
            const isErr = (s === 2)
            const isWarn = (s === 3)
            if (onlyErrors && onlyWarnings) return isErr || isWarn
            if (onlyErrors) return isErr
            if (onlyWarnings) return isWarn
            return true
          })
        }

        this.filesList = list
        for (const it of this.filesList) this.ensureServerThumb(it)
      } catch (e) {
        console.error(e)
        this.filesList = []
      } finally {
        this.loadingFiles = false
      }
    },
    formatDate(s) {
      if (!s) return '-'
      const d = new Date(s)
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = String(d.getFullYear())
      return `${dd}/${mm}/${yyyy}`
    },
    openPreview(id) {
      this.currentFileId = id
      this.showPreview = true
    },
    closePreview() {
      this.showPreview = false
      this.currentFileId = null
    },
    resetFilters() {
      if (!this.hasActiveFilters) return
      this.dateFrom = ''
      this.dateTo = ''
      this.filesFilters.q = ''
      this.filesFilters.onlyErrors = false
      this.filesFilters.onlyWarnings = false
      this.fetchFiles(1)
    },
    resetDates() {
      this.dateFrom = ''
      this.dateTo = ''
    },
    emitSubmit() {
      this.$emit('submit-range', { from: this.dateFrom || null, to: this.dateTo || null, filesCount: this.files.length })
    },
    async emitUpload() {
      if (!this.files.length || this.uploading) return
      const fd = new FormData()
      const allowed = /\.(pdf|jpe?g|png|webp|docx?|xlsx?)$/i
      const valid = []
      for (const f of this.files) {
        if (!allowed.test(f.name)) continue
        if (f.size > 20 * 1024 * 1024) continue
        valid.push(f.file)
      }
      if (!valid.length) {
        alert('Nu există fișiere valide de încărcat (accept: PDF/JPG/PNG/WEBP, max 20MB).')
        return
      }
      valid.forEach(file => fd.append('files[]', file))
      fd.append('firma_id', this.firmaId)

      this.uploading = true
      this.totalProgress = 0
      try {
        await axios.post(`${this.apiBase}/upload_altedocts`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (pe) => {
            if (!pe.total) return
            this.totalProgress = Math.round((pe.loaded * 100) / pe.total)
          },
          withCredentials: true,
        })
        this.clearAll()
        this.totalProgress = 0
        this.$emit('submit-range', { from: this.dateFrom || null, to: this.dateTo || null, filesCount: 0 })
        alert('Fișierele au fost încărcate cu succes.')
      } catch (e) {
        console.error(e)
        alert('Upload eșuat. Verifică tipurile/dimensiunea fișierelor sau autentificarea.')
      } finally {
        this.uploading = false
      }
    },

    onFileChange(e) {
      const selected = Array.from(e.target.files || [])
      this.addFiles(selected)
      e.target.value = ''
    },
    onDrop(e) {
      this.isDragging = false
      const dropped = Array.from(e.dataTransfer?.files || [])
      this.addFiles(dropped)
    },

    async addFiles(fileList) {
      for (const file of fileList) {
        const allowedExt = /\.(pdf|jpe?g|png|webp|docx?|xlsx?)$/i
        if (!allowedExt.test(file.name)) {
          console.warn('Tip neacceptat:', file.name)
          continue
        }
        if (file.size > 20 * 1024 * 1024) {
          console.warn('Fișier prea mare:', file.name)
          continue
        }
        const id = this.cryptoRandomId()
        const ext = (file.name.split('.').pop() || '').toLowerCase()
        const type = file.type
        const isImage = type?.startsWith('image/') || false
        const isPdf = (type && type.includes('pdf')) || ext === 'pdf'
        const isOffice = ['doc','docx','xls','xlsx'].includes(ext) ||
          type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          type === 'application/msword' ||
          type === 'application/vnd.ms-excel' ||
          type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

        const previewUrl = URL.createObjectURL(file)

        const record = {
          id,
          file,
          name: file.name,
          size: file.size,
          type,
          ext,
          isImage,
          isPdf,
          isOffice,
          previewUrl,
          thumbUrl: null,
          thumbPending: !!isPdf,
          serverUrl: null
        }
        this.files.push(record)

        if (isPdf) {
          this._pdfQueue.push({ file, record })
        }
      }
      this.$nextTick(() => setTimeout(() => {
        if (!this._pdfStarted) this._drainPdfQueue()
        else this._drainPdfQueue()
      }, 0))
    },

    removeFile(id) {
      const idx = this.files.findIndex(f => f.id === id)
      if (idx !== -1) {
        const f = this.files[idx]
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
        this.files.splice(idx, 1)
      }
    },
    clearAll() {
      this.files.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl) })
      this.files = []
    },

    friendlyType(f) {
      if (!f) return 'Fișier'
      const ext = (f.ext || '').toLowerCase()
      const mime = (f.type || '').toLowerCase()
      if (f.isImage || mime.startsWith('image/')) {
        if (ext === 'jpg' || ext === 'jpeg') return 'Imagine (JPG)'
        if (ext === 'png') return 'Imagine (PNG)'
        if (ext === 'webp') return 'Imagine (WEBP)'
        return 'Imagine'
      }
      if (f.isPdf || mime.includes('pdf') || ext === 'pdf') {
        return 'Document PDF'
      }
      if (ext === 'docx' || ext === 'doc' || mime.includes('msword') || mime.includes('wordprocessingml.document')) {
        return ext === 'doc' ? 'Document Word (DOC)' : 'Document Word (DOCX)'
      }
      if (ext === 'xlsx' || ext === 'xls' || mime.includes('vnd.ms-excel') || mime.includes('spreadsheetml.sheet')) {
        return ext === 'xls' ? 'Foaie de calcul Excel (XLS)' : 'Foaie de calcul Excel (XLSX)'
      }
      if (ext) return `Fișier ${ext.toUpperCase()}`
      if (mime) {
        if (mime.includes('text/plain')) return 'Fișier text (TXT)'
        if (mime.includes('json')) return 'Fișier JSON'
        if (mime.includes('zip')) return 'Arhivă ZIP'
      }
      return 'Fișier'
    },
    humanSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    cryptoRandomId() {
      try {
        const arr = new Uint32Array(2)
        crypto.getRandomValues(arr)
        return Array.from(arr).map(n => n.toString(16)).join('')
      } catch {
        return Math.random().toString(16).slice(2) + Date.now().toString(16)
      }
    },

    _drainPdfQueue() {
      this._pdfStarted = true
      if (this._pdfActive >= this._pdfMax) return
      while (this._pdfActive < this._pdfMax && this._pdfQueue.length) {
        const job = this._pdfQueue.shift()
        if (!job) break
        this._pdfActive++
        Promise.resolve().then(async () => {
          try {
            const thumb = await this._generatePdfThumbnailFast(job.file)
            job.record.thumbPending = false
            if (thumb) job.record.thumbUrl = thumb
          } catch (e) {
            console.warn('Thumb job failed:', e)
            job.record.thumbPending = false
          } finally {
            this._pdfActive--
            setTimeout(() => this._drainPdfQueue(), 0)
          }
        })
      }
    },

    async _generatePdfThumbnailFast(file) {
      const HARD_TIMEOUT_MS = 4500

      const renderWithOptions = async (opts) => {
        let loadingTask = null
        try {
          const arrayBuffer = await file.arrayBuffer()
          loadingTask = pdfjsLib.getDocument({
            data: arrayBuffer,
            stopAtErrors: true,
            ...opts,
          })

          const renderFirstPage = (async () => {
            const pdf = await loadingTask.promise
            const page = await pdf.getPage(1)
            const initialViewport = page.getViewport({ scale: 1 })
            const maxEdge = Math.max(initialViewport.width, initialViewport.height)
            const targetEdge = 180
            const scale = Math.max(0.1, Math.min(2.0, targetEdge / maxEdge))
            const viewport = page.getViewport({ scale })

            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = Math.ceil(viewport.width)
            canvas.height = Math.ceil(viewport.height)
            await page.render({ canvasContext: ctx, viewport }).promise
            return canvas.toDataURL('image/png')
          })()

          const timeout = new Promise((resolve) => setTimeout(() => resolve('__TIMEOUT__'), HARD_TIMEOUT_MS))
          const result = await Promise.race([renderFirstPage, timeout])

          if (result === '__TIMEOUT__') {
            try { await loadingTask?.destroy() } catch (_) {}
            return { ok: false, reason: 'timeout' }
          }
          return { ok: true, dataUrl: result || null }
        } catch (err) {
          try { await loadingTask?.destroy() } catch (_) {}
          return { ok: false, reason: err?.message || 'error' }
        }
      }

      let attempt = await renderWithOptions({})
      if (attempt.ok) return attempt.dataUrl
      attempt = await renderWithOptions({ disableWorker: true })
      if (attempt.ok) return attempt.dataUrl
      console.warn('PDF thumbnail failed fast-path:', attempt.reason)
      return null
    },

    openServerPreview(it) {
      this.serverPreviewItem = it
      this.filechange_form.reset();
      this.filechange_form.id = it.id;
      const val = Number(it?.stare_fisier ?? 0);
      const found = this.stateOptions.find(o => o.value === val) || { label: 'În așteptare', value: 0 };
      this.filechange_form.stare_fisier = found;
      this.filechange_form.alte_observatii = it?.alte_observatii || ''
      this.filechange_form.note = it?.note || ''
      this.editObservatii = false
      this.editNote = false
      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.suppressNextClick = false
      this.movedDuringDrag = false
      this.serverImgNaturalW = 0
      this.serverImgNaturalH = 0
      this.$nextTick(() => {
        const el = this.$refs.previewLeft
        if (el) {
          el.scrollTop = 0
          el.scrollLeft = 0
        }
      })
    },
    closeServerPreview() {
      this.serverPreviewItem = null
      this.serverZoomLevel = 0
      this.serverZoomScale = this.zoomScales[0]
      this.serverZoomed = false
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
      this.suppressNextClick = false
      this.movedDuringDrag = false
    },
    toggleServerZoom(e) {
      if (this.suppressNextClick) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
        relX = Math.max(0, Math.min(1, relX))
        relY = Math.max(0, Math.min(1, relY))
      }
      this.serverZoomLevel = (this.serverZoomLevel + 1) % this.zoomScales.length
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.isPreviewDragging = false
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop  = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop  = Math.max(0, targetTop)
      })
    },
    onWheelPreview(e) {
      if (!this.serverPreviewItem) return
      const el = this.$refs.previewLeft
      const img = this.$refs.previewImg
      let relX = 0.5, relY = 0.5
      if (img && e && img.getBoundingClientRect) {
        const rect = img.getBoundingClientRect()
        relX = (e.clientX - rect.left) / rect.width
        relY = (e.clientY - rect.top) / rect.height
      }
      const dir = e.deltaY > 0 ? -1 : 1
      let nextLevel = this.serverZoomLevel + dir
      nextLevel = Math.max(0, Math.min(this.zoomScales.length - 1, nextLevel))
      if (nextLevel === this.serverZoomLevel) return
      this.serverZoomLevel = nextLevel
      this.serverZoomScale = this.zoomScales[this.serverZoomLevel]
      this.serverZoomed = this.serverZoomLevel > 0
      this.$nextTick(() => {
        if (!el || !this.serverZoomed || !this.$refs.previewImg) {
          if (el && !this.serverZoomed) { el.scrollLeft = 0; el.scrollTop = 0 }
          return
        }
        const newImg = this.$refs.previewImg
        const newW = newImg.clientWidth
        const newH = newImg.clientHeight
        const targetLeft = newW * relX - el.clientWidth / 2
        const targetTop  = newH * relY - el.clientHeight / 2
        el.scrollLeft = Math.max(0, targetLeft)
        el.scrollTop  = Math.max(0, targetTop)
      })
    },
    onPreviewDragStart(e) {
      if (!this.serverZoomed) return
      if (e.button !== 0) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      this.isPreviewDragging = true
      this.movedDuringDrag = false
      this.previewDragStartX = e.clientX
      this.previewDragStartY = e.clientY
      this.previewScrollStartLeft = el.scrollLeft
      this.previewScrollStartTop  = el.scrollTop
      window.addEventListener('mousemove', this.onPreviewDragMove, { passive: false })
      window.addEventListener('mouseup', this.onPreviewDragEnd, { passive: false })
    },
    onPreviewDragMove(e) {
      if (!this.isPreviewDragging) return
      const el = this.$refs.previewLeft
      if (!el) return
      e.preventDefault()
      const dx = e.clientX - this.previewDragStartX
      const dy = e.clientY - this.previewDragStartY
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.movedDuringDrag = true
      el.scrollLeft = this.previewScrollStartLeft - dx
      el.scrollTop  = this.previewScrollStartTop  - dy
    },
    onPreviewDragEnd() {
      if (this.isPreviewDragging && this.movedDuringDrag) {
        this.suppressNextClick = true
        setTimeout(() => { this.suppressNextClick = false }, 0)
      }
      this.isPreviewDragging = false
      this.detachPreviewDragListeners()
    },
    detachPreviewDragListeners() {
      window.removeEventListener('mousemove', this.onPreviewDragMove)
      window.removeEventListener('mouseup', this.onPreviewDragEnd)
    },
    isImageMime(m) { return /^image\//i.test(m || '') },
    isPdfMime(m) { return /pdf/i.test(m || '') },
    isOfficeMime(m) { return /(msword|officedocument\.wordprocessingml\.document|vnd\.ms-excel|officedocument\.spreadsheetml\.sheet)/i.test(m || '') },

    _extractMonthKey(it) {
      // {id_firma}/altedocts/MM_YYYY/filename
      const p = (it.path || '')
      const m = p.match(/altedocts\/(\d{2}_\d{4})\//)
      return m ? m[1] : null
    },
    _monthKeyFromDate(iso) {
      if (!iso) return null
      const d = new Date(iso)
      const mm = (d.getMonth() + 1).toString().padStart(2, '0')
      const yyyy = d.getFullYear().toString()
      return `${mm}_${yyyy}`
    },
    _labelFromMonthKey(key) {
      if (!key) return 'Necunoscut'
      const [mm, yyyy] = key.split('_').map(Number)
      const monthNames = ['Ianuarie','Februarie','Martie','Aprilie','Mai','Iunie','Iulie','August','Septembrie','Octombrie','Noiembrie','Decembrie']
      const name = monthNames[(mm - 1) % 12] || mm
      return `${name} ${yyyy}`
    },
    statusClass(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'status-success'
        case 2: return 'status-fail'
        case 3: return 'status-warning'
        default: return 'status-pending'
      }
    },
    statusIcon(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'fa-solid fa-check'
        case 2: return 'fa-solid fa-xmark'
        case 3: return 'fa-solid fa-triangle-exclamation'
        default: return 'fa-solid fa-clock'
      }
    },
    statusLabel(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'Succes'
        case 2: return 'Eșec'
        case 3: return 'Avertizare'
        default: return 'În așteptare'
      }
    },
    statusBadgeClass(it) {
      const s = Number(it.stare_fisier)
      switch (s) {
        case 1: return 'bg-success'
        case 2: return 'bg-danger'
        case 3: return 'bg-warning text-dark'
        default: return 'bg-primary'
      }
    },

    serverThumbSrc(it) {
      const entry = this.serverThumbs[it.id]
      if (entry && entry.src) return entry.src
      this.ensureServerThumb(it)
      return this._placeholderSvgDataUrl(it)
    },
    async ensureServerThumb(it) {
      if (this.serverThumbs[it.id]?.pending || this.serverThumbs[it.id]?.src) return
      this.$set ? this.$set(this.serverThumbs, it.id, { src: '', pending: true }) : (this.serverThumbs[it.id] = { src: '', pending: true })
      try {
        if (this.isImageMime(it.mime)) {
          const src = `${this.apiBase}/stream_altedocts/${it.id}?firma_id=${this.firmaId}`
          this.serverThumbs[it.id].src = src
        } else if (this.isPdfMime(it.mime)) {
          const url = `${this.apiBase}/stream_altedocts/${it.id}`
          const resp = await axios.get(url, {
            responseType: 'arraybuffer',
            withCredentials: true,
            params: { firma_id: this.firmaId }
          })
          const dataUrl = await this._renderPdfThumbFromArrayBuffer(resp.data)
          this.serverThumbs[it.id].src = dataUrl || this._placeholderSvgDataUrl(it)
        } else {
          this.serverThumbs[it.id].src = this._placeholderSvgDataUrl(it)
        }
      } catch (e) {
        console.warn('ensureServerThumb failed for', it.id, e)
        this.serverThumbs[it.id].src = this._placeholderSvgDataUrl(it)
      } finally {
        this.serverThumbs[it.id].pending = false
        this.serverThumbs = { ...this.serverThumbs }
      }
    },
    async _renderPdfThumbFromArrayBuffer(arrayBuffer) {
      try {
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer, stopAtErrors: true })
        const pdf = await loadingTask.promise
        const page = await pdf.getPage(1)
        const initialViewport = page.getViewport({ scale: 1 })
        const maxEdge = Math.max(initialViewport.width, initialViewport.height)
        const targetEdge = 220
        const scale = Math.max(0.1, Math.min(2.0, targetEdge / maxEdge))
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return null
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        await page.render({ canvasContext: ctx, viewport }).promise
        return canvas.toDataURL('image/png')
      } catch (e) {
        console.warn('PDF client render failed:', e)
        return null
      }
    },
    _placeholderSvgDataUrl(it) {
      const ext = ((it.original_name || '').split('.').pop() || 'file').toUpperCase()
      const title = (it.original_name || '').slice(0, 40)
      const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="420" height="300" viewBox="0 0 420 300">
        <rect width="100%" height="100%" fill="#e9ecef"/>
        <g font-family="system-ui,-apple-system,Segoe UI,Roboto" fill="#495057" text-anchor="middle">
          <text x="210" y="150" font-size="56" font-weight="700">${ext}</text>
          <text x="210" y="190" font-size="14">${title.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</text>
        </g>
      </svg>`
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
    },
    onPreviewImgLoaded(e) {
      const img = e?.target
      if (!img) return
      this.serverImgNaturalW = img.naturalWidth || 0
      this.serverImgNaturalH = img.naturalHeight || 0
    },
    openPopupPreview(item) {
      if (!item) return
      const url = `${this.apiBase}/stream_altedocts/${item.id}?firma_id=${this.firmaId}`
      const width = 1000
      const height = 800
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      window.open(url, 'PopupPreview', `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`)
    },
    async updateServerItem() {
      if (!this.serverPreviewItem) return
      this.$Progress.start();
      try {
        this.filechange_form.firma_id = this.firmaId
        if (this.filechange_form.stare_fisier && typeof this.filechange_form.stare_fisier === 'object') {
          this.filechange_form.stare_fisier = this.filechange_form.stare_fisier.value;
        }
        await this.filechange_form.post(`${this.apiBase}/update_altedocts/${this.serverPreviewItem.id}`)
        toast.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Fișierul a fost actualizat cu succes!',
          showConfirmButton: false,
          timer: 1200
        });
        this.$Progress.finish();

        // sincronizează UI-ul local
        this.serverPreviewItem.stare_fisier = Number(this.filechange_form.stare_fisier)
        this.serverPreviewItem.alte_observatii = this.filechange_form.alte_observatii
        this.serverPreviewItem.note = this.filechange_form.note

        const idx = this.filesList.findIndex(x => x.id === this.serverPreviewItem.id)
        if (idx !== -1) {
          const updated = {
            ...this.filesList[idx],
            stare_fisier: Number(this.filechange_form.stare_fisier),
            alte_observatii: this.filechange_form.alte_observatii,
            note: this.filechange_form.note,
          }
          if (this.$set) this.$set(this.filesList, idx, updated)
          else this.filesList.splice(idx, 1, updated)
        }
      } catch (e) {
        console.error(e);
        const errMsg = e?.response?.data?.message || e.message || 'Actualizarea a eșuat.';
        toast.fire({
          position: 'top-end',
          icon: 'error',
          title: `Eroare: ${errMsg}`,
          showConfirmButton: false,
          timer: 2500
        });
        this.$Progress.fail();
      }
    },
  }
}
</script>

<style scoped>
/* Small polish for previews */
.ratio { width: 100%; }

/* Modal preview styles */
.preview-modal { position: fixed; inset: 0; z-index: 1050; }
.preview-backdrop { position: absolute; inset: 0; background: rgba(0,0,0,0.5); }
.preview-content {
  position: relative; max-width: 1400px; width: 95vw; max-height: 90vh;
  display: flex; flex-direction: column;
}
/* Make body the scrollable area */
.preview-content .card-body { flex: 1 1 auto; overflow: hidden; max-height: calc(90vh - 60px); }
.preview-content img { max-width: 100%; height: auto; display: block; }
.pdf-preview iframe { width: 100%; border: 0; min-height: 70vh; }

/* Split modal layout */
.preview-split { height: calc(90vh - 60px); }
.preview-split-left { border-right: 1px solid rgba(0,0,0,.1); background: #fff; overflow: hidden; }
.preview-split-right { background: #fff; overflow: auto; max-height: calc(90vh - 60px); }
.preview-split-left, .preview-split-right { height: 100%; }
[data-bs-theme="dark"] .preview-split-left,
[data-bs-theme="dark"] .preview-split-right { background: #121212 !important; border-color: rgba(255,255,255,.12) !important; }

/* Thumbnails grid for server files */
.thumb-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px; }
.thumb-card { border: 1px solid rgba(0,0,0,.125); border-radius: .5rem; overflow: hidden; display: flex; flex-direction: column; background: #fff; }
.thumb-media { position: relative; background: #f8f9fa; aspect-ratio: 4 / 3; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.thumb-media img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Status pill overlay on thumbnail */
.thumb-status {
  position: absolute; top: .5rem; left: .5rem; width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: .9rem;
  box-shadow: 0 2px 6px rgba(0,0,0,.2); border: 1px solid rgba(0,0,0,.1); z-index: 2;
}
.status-pending { background: #0d6efd; color: #fff; }
.status-success { background: #198754; color: #fff; }
.status-fail    { background: #dc3545; color: #fff; }
.status-warning { background: #ffc107; color: #212529; border-color: rgba(0,0,0,.2); }
[data-bs-theme="dark"] .status-warning { color: #212529; }

.thumb-body { padding: .5rem .75rem 0; font-size: .9rem; }
.thumb-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 600; }
.thumb-meta { font-size: .8rem; }
.thumb-actions { padding: .5rem .75rem .75rem; display: flex; gap: .5rem; }

/* Hide native calendar icon to avoid white-on-white issues */
input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0 !important; }
input[type="date"] { color-scheme: light; }
/* Custom calendar button inside the input */
.date-picker-btn {
  position: absolute; right: .5rem; top: 50%; transform: translateY(-50%); line-height: 1; padding: .25rem .45rem;
}
/* Dark theme adjustment only for the custom date picker button */
[data-bs-theme="dark"] .date-picker-btn {
  background-color: #1f1f1f !important; color: #e9ecef !important; border-color: rgba(255,255,255,.15) !important;
}
[data-bs-theme="dark"] .date-picker-btn:hover { background-color: #262626 !important; }
@media (prefers-color-scheme: dark) {
  :root:not([data-bs-theme="light"]) .date-picker-btn {
    background-color: #1f1f1f !important; color: #e9ecef !important; border-color: rgba(255,255,255,.15) !important;
  }
  :root:not([data-bs-theme="light"]) .date-picker-btn:hover { background-color: #262626 !important; }
}

/* === Dark theme fixes for thumbnail area (local only) === */
[data-bs-theme="dark"] .thumb-card { background: #121212 !important; border-color: rgba(255,255,255,.12) !important; }
[data-bs-theme="dark"] .thumb-media { background: #1a1a1a !important; }
[data-bs-theme="dark"] .thumb-title { color: #e9ecef !important; }
[data-bs-theme="dark"] .thumb-meta { color: rgba(233,236,239,.6) !important; }

/* Image zoom in server preview */
.preview-split-left.zoom-enabled { overflow: auto; cursor: grab; user-select: none; }
.preview-split-left.dragging { cursor: grabbing; }
.preview-image { max-width: 100%; height: auto; display: block; transition: width .15s ease; cursor: zoom-in; -webkit-user-drag: none; user-select: none; }
.preview-image.is-zoomed { cursor: grab; }
.pannable { width: 100%; height: 100%; }
.pannable.centered { display: flex; align-items: center; justify-content: center; }
.pannable.centered .preview-image { width: 100%; height: 100%; object-fit: contain; }
.preview-split-left.zoom-enabled .pannable { display: block; min-width: max-content; min-height: max-content; }
</style>